import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Extracts a display-safe message from an unknown error.
 * @param err - Caught value (unknown)
 * @param fallback - Message to use when err has no .message
 */
export function getErrorMessage(err: unknown, fallback: string): string {
  if (err != null && typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return fallback;
}

export const createActionName = (componentName: string, actionName: string) => {
  return componentName + "/trigger" + actionName;
};

export const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString();
};

export const formatDateTime = (date: Date | string) => {
  return new Date(date).toLocaleString();
};

/**
 * Sets a value in local storage.
 * @param key The key under which to store the item.
 * @param value The value to store; must be serializable as JSON.
 */
export function setItem(key: string, value: any): void {
  try {
    let serializedValue: string;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      serializedValue = String(value);
    } else {
      serializedValue = JSON.stringify(value);
    }
    localStorage.setItem(key, serializedValue);
  } catch (error) {
    console.error("Error saving to localStorage", error);
  }
}

/**
 * Retrieves a value from local storage.
 * @param key The key under which the item is stored.
 * @returns The deserialized stored value or null if not found or on error.
 */
export function getItem<T>(key: string): T | null {
  try {
    const serializedItem = localStorage.getItem(key);
    if (!serializedItem) return null;

    // Try to parse as JSON first
    try {
      return JSON.parse(serializedItem) as T;
    } catch (parseError) {
      // If JSON parsing fails, return the raw string value
      return serializedItem as T;
    }
  } catch (error) {
    return null;
  }
}

/**
 * Removes a value from local storage.
 * @param key The key under which the item is stored that should be removed.
 */
export function removeItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
  }
}

/**
 * Clears all entries from local storage.
 */
export function clear(): void {
  try {
    localStorage.clear();
  } catch (error) {
  }
}

/**
 * Calculate validity status based on start and end dates
 * @param startDate - Validity start date (string or Date)
 * @param endDate - Validity end date (string or Date)
 * @returns 'active' | 'expired' | 'pending'
 */
export function calculateValidityStatus(
  startDate: string | Date,
  endDate: string | Date
): 'active' | 'expired' | 'pending' {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  if (end < today) {
    return 'expired';
  } else if (start > today) {
    return 'pending';
  } else {
    return 'active';
  }
}